import React, { useCallback, useState } from 'react';
import { AutoComplete } from 'antd';
import classNames from 'classnames';
import { debounce } from 'lodash';
import { FieldInputProps, FieldMetaProps, FormikProps } from 'formik';
import { SelectValue } from 'antd/lib/select';

interface OptionType {
  value: string | number;
  text: string;
  component?: React.ReactNode;
}

type FilterFunc<T> = (inputValue: string, option?: T) => boolean;
type OptionData = {
  label?: React.ReactNode;
  value?: string | number;
};
type OptionGroupData = {
  label?: React.ReactNode;
  options?: OptionData[];
};

type Props = {
  // Пропсы от Formik
  field: FieldInputProps<any>;
  form: FormikProps<any>;
  meta: FieldMetaProps<any>;

  // Пропсы от AutoComplete
  options: OptionType[];
  placeholder?: string;
  allowClear?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
  className?: string;
  filterOption?: boolean | FilterFunc<OptionData | OptionGroupData>;
  onFocus?: React.FocusEventHandler<HTMLElement>;
  onBlur?: React.FocusEventHandler<HTMLElement>;
  autoClearSearchValue?: boolean;
  defaultActiveFirstOption?: boolean;
  dropdownMatchSelectWidth?: boolean | number;
  notFoundContent?: React.ReactNode;

  // Кастомные пропсы
  label?: string;
  fieldErrors: string | null;
  pause?: number;
  onChange: (value: SelectValue) => void,
  onSelect?: (value: SelectValue) => void,
  onSearch: (value: string) => void,
};

const CustomAutoCompleteField: React.FC<Props> = ({
  field,
  form,
  meta,
  options,
  placeholder,
  onSelect,
  onChange,
  onSearch,
  filterOption = false,
  defaultActiveFirstOption = false,
  pause = 800,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const { label } = props;

  const handleSelect = (val: SelectValue) => {
    if (onSelect) onSelect(val);
  };

  const handleSearch = useCallback(
    debounce(onSearch, pause),
    [onSearch],
  );

  const handleBlur = (e: React.FocusEvent<HTMLElement>) => {
    setIsFocused(false);
    if (props.onBlur) {
      props.onBlur(e);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLElement>) => {
    setIsFocused(true);
    if (props.onFocus) {
      props.onFocus(e);
    }
  };

  const { fieldErrors } = props;

  return (
    <>
      <div className={
              classNames(
                'custom-select-field gx-flex-column', {
                  'is-filled': isFocused || field.value,
                  'is-focused': isFocused,
                  'is-error': !!fieldErrors,
                },
              )
          }
      >
        { !!label && (
          <label htmlFor={field.name} className="custom-select-field__label">{ label }</label>
        ) }
        <AutoComplete
          {...field}
          {...props}
          onChange={onChange}
          onSelect={handleSelect}
          onSearch={handleSearch}
          filterOption={filterOption}
          value={field.value}
          defaultActiveFirstOption={defaultActiveFirstOption}
          onBlur={handleBlur}
          onFocus={handleFocus}
          className="custom-select-field__input"
        >
          { options.map((res) => (
            <AutoComplete.Option value={res.value} key={res.value}>
              { res.component ?? res.text }
            </AutoComplete.Option>
          )) }
        </AutoComplete>
      </div>
      { !!fieldErrors && (
        <div className="custom-field__error is-map-error">{ fieldErrors }</div>
      ) }
    </>
  );
};

export default CustomAutoCompleteField;
